import React, { useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * RichTextEditor — WordPress-style editor with image upload support.
 * Uploads images through the /api/uploads endpoint (Emergent Object Storage).
 */
export default function RichTextEditor({ value, onChange, placeholder = "İçeriği yazın…", testid = "rich-editor" }) {
  const quillRef = useRef(null);

  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        toast.loading("Görsel yükleniyor…", { id: "img-up" });
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // Insert the RESOLVED absolute URL in the editor content so it renders
        // correctly in the editor. Rich content is stored as HTML so images
        // survive domain migrations via the fileUrl() normalizer at read time.
        const url = fileUrl(data.url);
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        editor?.insertEmbed(range?.index ?? 0, "image", url, "user");
        editor?.setSelection((range?.index ?? 0) + 1);
        toast.success("Görsel eklendi", { id: "img-up" });
      } catch (e) {
        toast.error("Görsel yüklenemedi", { id: "img-up" });
      }
    };
    input.click();
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    clipboard: { matchVisual: false },
  }), []);

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "color", "background", "list", "align",
    "blockquote", "code-block", "link", "image", "video",
  ];

  return (
    <div data-testid={testid} className="rich-editor-wrapper">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
}
